// avatar.js
//
// One renderer for "how a person is pictured", used by the contacts page, the
// setup onboarding steps, account settings, and the Alert Resolution Center. A
// person should look identical everywhere they appear, so every surface takes the
// same identity block from the API rather than each deciding for itself.
//
// An identity block (Users::getIdentity() / Contacts::decorateWithProfile()):
//   { display_name, initials, avatar_type, avatar_url, avatar_version, avatar_seed }
//
// Three styles:
//   letters   initials on a colour derived from the seed (the default)
//   abstract  generated art, deterministic from the seed — nothing is stored or
//             hosted, so it costs nothing and can never 404
//   image     an uploaded picture at avatar_url

/**
 * FNV-1a, 32-bit. Small, fast, and stable across browsers — which matters more
 * than distribution here, because the same person must get the same art on every
 * device and in every session.
 */
const hashSeed = (value) => {

  const input = String(value == null ? '' : value).trim().toLowerCase();

  let hash = 0x811c9dc5;

  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // ">>> 0" keeps it an unsigned 32-bit int through the multiply
    hash = (hash * 0x01000193) >>> 0;
  }

  // Avalanche (MurmurHash3's fmix32). FNV-1a's low bits barely move between
  // similar inputs, so picking a motif with `hash % 8` — only the low 3 bits —
  // gave a wildly lopsided spread: mostly two motifs. This mixes every bit into
  // every other so small modulos are evenly distributed.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b) >>> 0;
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35) >>> 0;
  hash ^= hash >>> 16;

  return hash >>> 0;

};

/**
 * Deterministic PRNG seeded from the hash (mulberry32), so a seed yields the same
 * sequence of shapes every time.
 */
const makeRandom = (seed) => {

  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

};

// ---- small procedural helpers -----------------------------------------------

const range = (random, min, max) => min + random() * (max - min);
const rangeInt = (random, min, max) => Math.floor(min + random() * (max - min + 1));
const chance = (random, probability) => random() < probability;
const pick = (random, list) => list[Math.floor(random() * list.length)];
const n = value => Math.round(value * 10) / 10;

/**
 * A colour pair generated from a hue rather than chosen from a fixed list.
 *
 * Saturation and lightness are pinned to a band that always holds white
 * silhouettes and white text, so any hue is safe — which buys a continuous colour
 * space instead of ten options, without risking a muddy or low-contrast result.
 */
const palette = (random) => {

  const hue = Math.floor(random() * 360);
  const drift = range(random, 18, 52) * (chance(random, 0.5) ? 1 : -1);

  // Yellow-greens (around 85deg) turn acidic at the saturation that suits blues
  // and purples, so pull saturation and lightness back as a hue approaches that
  // band. Without this a continuous hue space throws out a few harsh limes.
  const distance = Math.abs(((hue - 85 + 540) % 360) - 180);
  const acid = Math.max(0, 1 - (180 - distance) / 45);

  const sat = range(random, 52, 68) - acid * 16;

  return [
    `hsl(${hue} ${n(sat)}% ${n(range(random, 35, 44) - acid * 5)}%)`,
    `hsl(${Math.round((hue + drift + 360) % 360)} ${n(sat + 8)}% ${n(range(random, 56, 66) - acid * 6)}%)`,
  ];

};

export const paletteForSeed = (seed) => palette(makeRandom(hashSeed(seed)));

export const escapeHtml = (value) =>
  String(value == null ? '' : value).replace(/[&<>"']/g, char => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case '\'': return '&#39;';
      default: return char;
    }
  });

/**
 * Generated artwork: an 8x8 grid, one solid colour per cell.
 *
 * Replaces an earlier motif system (parametric trees, cats, fruit rendered through
 * a blurred mask). That drew attention to itself as illustration; this reads as a
 * designed pattern — quiet enough for a professional UI, distinct enough that the
 * one belonging to you is recognisably yours.
 *
 * Each cell's colour comes from sampling a cheap scalar field at the cell centre
 * and quantising the result onto a small colour ramp. The field is a sum of a few
 * plane waves plus an optional radial term, which is what produces stripes,
 * checks, diagonals, interference bands and concentric blooms rather than noise.
 *
 * Cost: 64 solid <rect>s, no filters, no masks, no patterns. Cheaper to rasterise
 * than the two Gaussian blurs it replaces, which matters on a list of avatars.
 */

const GRID = 8;

/**
 * Symmetry applied to cell coordinates before sampling.
 *
 * This is what separates "designed" from "random speckle" — a folded field reads
 * as intentional even though the field itself is arbitrary. Kept as coordinate
 * folds rather than post-processing so it costs nothing.
 */
const SYMMETRIES = {

  none: (x, y) => [x, y],

  // mirror down the vertical centre line
  mirrorX: (x, y) => [Math.min(x, GRID - 1 - x), y],

  mirrorY: (x, y) => [x, Math.min(y, GRID - 1 - y)],

  // four-fold: fold into one quadrant
  quad: (x, y) => [Math.min(x, GRID - 1 - x), Math.min(y, GRID - 1 - y)],

  // 180 degree rotation about the centre
  rotate: (x, y) => (y * GRID + x < (GRID * GRID) / 2)
    ? [x, y]
    : [GRID - 1 - x, GRID - 1 - y],

  // reflect across the leading diagonal
  diagonal: (x, y) => (x > y) ? [y, x] : [x, y],

};

export const SYMMETRY_NAMES = Object.keys(SYMMETRIES);

/**
 * Build the field sampler for a seed.
 *
 * Returns a function of (x, y) cell coordinates giving a value in 0..1.
 */
const buildField = (random) => {

  // 2-3 plane waves. One wave alone gives stripes; two interfere into checks and
  // lattices; three start to feel woven.
  const waveCount = rangeInt(random, 2, 3);
  const waves = [];

  for (let i = 0; i < waveCount; i++) {
    waves.push({
      // snapping some angles to 45 degree steps keeps a share of the output
      // crisply aligned to the grid, which reads more deliberate than all-oblique
      angle: chance(random, 0.45)
        ? (Math.floor(random() * 8) * Math.PI) / 4
        : random() * Math.PI * 2,
      freq: range(random, 0.55, 2.4),
      phase: random() * Math.PI * 2,
      amp: range(random, 0.6, 1),
    });
  }

  // a radial term turns the composition into rings or a bloom around a point
  const radial = chance(random, 0.42)
    ? {
      cx: range(random, 1.5, GRID - 1.5),
      cy: range(random, 1.5, GRID - 1.5),
      freq: range(random, 0.5, 1.5),
      phase: random() * Math.PI * 2,
      amp: range(random, 0.7, 1.3),
    }
    : null;

  let total = waves.reduce((sum, wave) => sum + wave.amp, 0);

  if (radial) {
    total += radial.amp;
  }

  return (x, y) => {

    let value = 0;

    for (let i = 0; i < waves.length; i++) {
      const wave = waves[i];
      const projected = x * Math.cos(wave.angle) + y * Math.sin(wave.angle);
      value += Math.sin(projected * wave.freq + wave.phase) * wave.amp;
    }

    if (radial) {
      const dx = x - radial.cx;
      const dy = y - radial.cy;
      value += Math.sin(Math.sqrt(dx * dx + dy * dy) * radial.freq + radial.phase) * radial.amp;
    }

    // sum of sines is in -total..total; normalise to 0..1
    return (value / total + 1) / 2;

  };

};

/**
 * A ramp of solid colours from one hue.
 *
 * Levels step in lightness and drift slightly in hue, so a pattern reads as tonal
 * variations of one colour rather than a clash. Saturation and lightness stay in a
 * band that looks composed at any hue, and the yellow-green damping is the same
 * correction the letters palette uses.
 */
const buildRamp = (random, levels) => {

  const hue = Math.floor(random() * 360);
  const hueSpan = range(random, 10, 46) * (chance(random, 0.5) ? 1 : -1);

  const distance = Math.abs(((hue - 85 + 540) % 360) - 180);
  const acid = Math.max(0, 1 - (180 - distance) / 45);

  // Muted on purpose. Fully saturated ramps read as a game tile set; this band
  // keeps the patterns quiet enough to sit beside a contact's name without
  // competing with it, while still giving each person an obvious colour identity.
  const sat = range(random, 26, 52) - acid * 12;
  const darkest = range(random, 30, 42) - acid * 4;
  const lightest = range(random, 66, 79) - acid * 6;

  const ramp = [];

  for (let i = 0; i < levels; i++) {
    const t = levels === 1 ? 0.5 : i / (levels - 1);
    ramp.push(
      `hsl(${Math.round((hue + hueSpan * t + 360) % 360)} ` +
      `${n(sat + (1 - Math.abs(t - 0.5) * 2) * 4)}% ` +
      `${n(darkest + (lightest - darkest) * t)}%)`
    );
  }

  return ramp;

};

/**
 * A fresh random seed for the artwork, for the "Shuffle" control.
 *
 * The seed is what gets stored (as users_meta.avatar_art_seed), so a chosen
 * picture is stable afterwards — still generated, still nothing hosted.
 */
export const randomArtSeed = () =>
  Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);

/**
 * Generated artwork for a seed, as an inline SVG string.
 *
 * Deterministic: the same seed always yields the same picture, so a person's
 * avatar is stable across devices and sessions.
 */
export const abstractAvatarSvg = (seed, { size = 96, rounded = true } = {}) => {

  const hash = hashSeed(seed);
  const random = makeRandom(hash);

  // Drawn from the PRNG in a fixed order so the same seed always rebuilds the
  // same picture.
  const symmetry = chance(random, 0.78)
    ? pick(random, SYMMETRY_NAMES.filter(name => name !== 'none'))
    : 'none';

  // Few levels read as bold blocks, more as a gradient wash. Both are wanted.
  const levels = rangeInt(random, 3, 6);
  const ramp = buildRamp(random, levels);
  const field = buildField(random);
  const fold = SYMMETRIES[symmetry];

  const cell = 100 / GRID;
  const cells = [];

  for (let y = 0; y < GRID; y++) {
    for (let x = 0; x < GRID; x++) {

      const [fx, fy] = fold(x, y);
      const value = field(fx + 0.5, fy + 0.5);
      const level = Math.max(0, Math.min(levels - 1, Math.floor(value * levels)));

      cells.push(
        `<rect x="${n(x * cell)}" y="${n(y * cell)}" width="${n(cell)}" height="${n(cell)}" ` +
        `fill="${ramp[level]}"/>`
      );

    }
  }

  // shape-rendering="crispEdges" removes the hairline seams antialiasing leaves
  // between abutting cells, which is what makes the grid look solid
  return `<svg class="avatar__art" data-symmetry="${symmetry}" data-levels="${levels}" ` +
    `viewBox="0 0 100 100" width="${size}" height="${size}" ` +
    `role="img" aria-hidden="true" focusable="false" preserveAspectRatio="xMidYMid slice" ` +
    `shape-rendering="crispEdges">` +
    (rounded
      ? `<defs><clipPath id="av${hash.toString(36)}c">` +
        `<rect width="100" height="100" rx="24"/></clipPath></defs>` +
        `<g clip-path="url(#av${hash.toString(36)}c)">${cells.join('')}</g>`
      : cells.join('')) +
    `</svg>`;

};

// Hosts where Cloudflare Image Transformations are enabled. Anything else — a
// legacy files.mytempstick.com avatar, a data: URI in a test harness — is served
// untouched rather than pointed at a /cdn-cgi/image path that would 404.
const TRANSFORM_HOSTS = ['img.mytempstick.com'];

// Transformations are billed per *unique* transformation, not per request, so the
// app deliberately asks for a tiny fixed set of widths rather than mirroring every
// CSS size. Every avatar in the app displays at <=96px, so in practice only 96 and
// 192 are ever generated.
const VARIANT_WIDTHS = [96, 192, 384];

/**
 * The variant to request for a given display size, at 2x for retina.
 * 24-48px -> 96, 64-96px -> 192.
 */
const variantWidth = (size) => {

  const target = Math.max(1, Math.round(size)) * 2;

  return VARIANT_WIDTHS.find(width => width >= target) ||
    VARIANT_WIDTHS[VARIANT_WIDTHS.length - 1];

};

/**
 * Cache-bust an uploaded avatar on change.
 *
 * Only used for untransformed URLs. Object keys are random per upload so the URL
 * already changes on its own; this is insurance for any legacy path where it
 * might not.
 */
const versionedUrl = (url, version) => {

  const clean = String(url || '').trim();

  if (!clean || !version) {
    return clean;
  }

  // a query string is part of the payload in a data: URI, not a parameter —
  // appending one corrupts the image
  if (clean.startsWith('data:')) {
    return clean;
  }

  return clean + (clean.includes('?') ? '&' : '?') + 'v=' + encodeURIComponent(version);

};

/**
 * The URL to actually put in an <img src> for a stored avatar.
 *
 * On a transform-enabled host this rewrites to Cloudflare's resizing path, which
 * serves AVIF or WebP per the browser's Accept header and strips any remaining
 * EXIF. Option order is fixed so the same variant always hits the same cache key.
 *
 * No version query is added to a transformed URL: the key is already unique per
 * upload, and a changing query string would mint a fresh unique transformation
 * every time for no benefit.
 */
export const avatarImageUrl = (url, size, version) => {

  const clean = String(url || '').trim();

  if (!clean) {
    return '';
  }

  let parsed;

  try {
    parsed = new URL(clean, window.location.origin);
  } catch (e) {
    return versionedUrl(clean, version);
  }

  if (!TRANSFORM_HOSTS.includes(parsed.hostname)) {
    return versionedUrl(clean, version);
  }

  // already a transform path (double-rewriting would nest the options)
  if (parsed.pathname.startsWith('/cdn-cgi/image/')) {
    return clean;
  }

  const width = variantWidth(size);

  const options = [
    `width=${width}`,
    `height=${width}`,
    'fit=cover',
    'format=auto',
    'quality=85',
    'metadata=none',
  ].join(',');

  return `${parsed.origin}/cdn-cgi/image/${options}${parsed.pathname}`;

};

/**
 * What to call a person, from a contact or a user.
 *
 * display_name is the API's answer (Contacts::decorateWithProfile) and it already
 * prefers the profile that person maintains themselves — the account owner's or
 * the sub user's, whichever they are — falling back to contact_name only while
 * that profile has no name in it. So every surface that shows a contact uses this
 * and never contact_name directly, or the same person ends up with two names in
 * one session.
 */
export const displayNameFor = (source = {}) => String(
  (source && (source.display_name || source.contact_name || source.email)) || ''
).trim() || 'Unnamed';

/**
 * Normalise anything person-shaped into an identity block.
 *
 * Accepts a decorated contact, a user, or a partial object, so call sites don't
 * have to care which endpoint their data came from.
 */
export const toIdentity = (source = {}) => {

  const name = displayNameFor(source);

  const email = String(source.email || '').trim();

  let type = String(source.avatar_type || 'letters');
  const url = String(source.avatar_url || '').trim();

  // never render a broken picture
  if (type === 'image' && !url) {
    type = 'letters';
  }

  if (type !== 'image' && type !== 'abstract') {
    type = 'letters';
  }

  return {
    display_name: name,
    initials: String(source.initials || initialsFor(name, email)),
    avatar_type: type,
    avatar_url: url,
    avatar_version: source.avatar_version || 0,
    avatar_seed: String(source.avatar_seed || email || name).toLowerCase(),
  };

};

export const initialsFor = (name, email = '') => {

  const source = String(name || email || '').trim();

  if (!source) {
    return '?';
  }

  // Punctuation is dropped before the letters are taken, so "O'Brien" gives OB
  // rather than O' and "Denny's" gives DE. Names carry apostrophes and hyphens
  // and a tile showing one reads as a rendering fault. Mirrors
  // Users::getInitials() server-side.
  const parts = source
    .split(/\s+/)
    .map(part => part.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);

  if (!parts.length) {
    return '?';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();

};

/**
 * Markup for a person's avatar.
 *
 * @param {object} source  identity block, contact, or user
 * @param {object} options size in px, extra class, and whether to add a title
 * @returns {string} HTML
 */
export const renderAvatar = (source, { size = 40, cls = '', title = false } = {}) => {

  const identity = toIdentity(source);
  const safeName = escapeHtml(identity.display_name);

  const classes = ['avatar', `avatar--${identity.avatar_type}`];

  if (cls) {
    classes.push(cls);
  }

  // the wrapper carries the box; each style fills it
  const open = `<span class="${classes.join(' ')}" style="width:${size}px;height:${size}px;"` +
    (title ? ` title="${safeName}"` : '') + '>';

  if (identity.avatar_type === 'image') {

    const src = escapeHtml(
      avatarImageUrl(identity.avatar_url, size, identity.avatar_version)
    );

    return open +
      `<img class="avatar__image" src="${src}" alt="${safeName}" ` +
      `width="${size}" height="${size}" loading="lazy" />` +
      '</span>';

  }

  if (identity.avatar_type === 'abstract') {
    return open + abstractAvatarSvg(identity.avatar_seed, { size }) + '</span>';
  }

  const [dark, light] = paletteForSeed(identity.avatar_seed);

  return open +
    `<span class="avatar__letters" aria-hidden="true" ` +
    `style="background:linear-gradient(135deg,${light},${dark});` +
    `font-size:${Math.max(10, Math.round(size * 0.36))}px;">` +
    escapeHtml(identity.initials) +
    '</span></span>';

};

export default renderAvatar;
